import cx from 'classnames';
import { useCallback } from 'react';

/**
 * A clickable navigation card the agent offers when it wants to point the
 * operator at a place in the app ("teach the pick position", "open the routine
 * you just created", "configure a gripper"). It's the prettier alternative to a
 * raw markdown link.
 *
 * Clicking it calls `onNavigate(url)` — the chat mount wires that to
 * react-router's `navigate`. The chat panel is mounted above the route outlet,
 * so navigating swaps the page **behind** the still-open conversation; the chat
 * never closes. This is purely a navigation affordance — it does NOT paste a
 * message or send anything (unlike ConfirmationCard).
 */
export interface NavigationCardProps {
  label: string;
  url: string;
  onNavigate: (url: string) => void;
}

export function NavigationCard({
  label,
  url,
  onNavigate,
}: NavigationCardProps) {
  const handleClick = useCallback(() => {
    onNavigate(url);
  }, [onNavigate, url]);

  return (
    // 14px top margin isn't in the spacing scale (12 → 16) — matches
    // ConfirmationCard so stacked cards align.
    <div style={{ marginTop: 14 }}>
      <div
        className="tw-rounded-14 tw-overflow-hidden"
        style={{
          background: 'var(--surface-primary-elevated)',
          border: '1px solid var(--divider-primary)',
        }}
      >
        <button
          type="button"
          onClick={handleClick}
          aria-label={`Go to ${label}`}
          data-testid="chat-navigation-card-button"
          className={cx(
            'tw-flex',
            'tw-items-center',
            'tw-justify-between',
            'tw-gap-8',
            'tw-w-full',
            'tw-border-none',
            'tw-outline-none',
            'tw-text-left',
          )}
          style={{
            // 14px padding isn't in the spacing scale (12 → 16).
            padding: '14px 14px',
            background: 'transparent',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            fontFamily: 'inherit',
          }}
        >
          <div
            className="tw-font-medium tw-min-w-0 tw-truncate"
            style={{
              // 14/`-0.2` letter-spacing aren't in the typography scale.
              fontSize: 14,
              color: 'var(--label-primary)',
              letterSpacing: -0.2,
            }}
          >
            {label}
          </div>
          {/* Arrow-up-right glyph — signals "opens that page". Inline SVG
              (like ConfirmationCard's checkmark) to avoid coupling to a
              specific design-system IconKind. */}
          <svg
            width={14}
            height={14}
            viewBox="0 0 16 16"
            aria-hidden="true"
            className="tw-shrink-0"
            style={{ fill: 'none', stroke: 'var(--label-tertiary)' }}
          >
            <path
              d="M5 11L11 5M11 5H6M11 5V10"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}
